/**
 * @module Resume
 * @description This module defines the Resume component for the portfolio application.
 * The Resume component is a functional React component that renders a detailed resume
 * section of the portfolio, including education, research, work experience, and projects.
 * It fetches data from a backend API and displays the information in a structured format.
 *
 * The component uses React Bootstrap's Container, Row, and Col components for layout and styling.
 * It also utilizes custom button components for linking to external resources such as ArXiv, GitHub, posters, and slides.
 *
 * The Resume component handles data fetching with retry mechanisms and displays a loading state while data is being fetched.
 *
 * @component
 * @returns {JSX.Element} The rendered Resume component.
 */

import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import "../../css/Homepage.css";
import "../../css/Resume.css";
import {
  getEducation,
  getExperience,
  getProjects,
  getResearch,
  getResearchInterests,
  getResearchPillars,
} from "../../services/portfolioService";
import { sanitizeHTML } from "../../utils/portfolioUtils";
import LoadingState from "../common/LoadingState";
import EducationItem from "./items/EducationItem";
import ExperienceItem from "./items/ExperienceItem";
import ProjectItem from "./items/ProjectItem";
import ResearchItem from "./items/ResearchItem";

/**
 * Resume component that renders a detailed resume section of the portfolio.
 *
 * @component
 * @returns {JSX.Element} The rendered Resume component.
 */
const Resume = () => {
  const [education, setEducation] = useState([]);
  const [research, setResearch] = useState([]);
  const [experience, setExperience] = useState([]);
  const [projects, setProjects] = useState([]);
  const [researchPillars, setResearchPillars] = useState([]);
  const [researchInterests, setResearchInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  /**
   * Fetches data for the resume section from the backend API.
   * The data includes research interests, education, research, experience, and projects.
   * If the request fails, it retries up to 5 times with a delay of 5 seconds between retries.
   */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          pillarsData,
          interestsData,
          educationData,
          researchData,
          experienceData,
          projectsData,
        ] = await Promise.all([
          getResearchPillars(),
          getResearchInterests(),
          getEducation(),
          getResearch(),
          getExperience(),
          getProjects(),
        ]);

        setResearchPillars(pillarsData);
        setResearchInterests(interestsData);
        setEducation(educationData);
        setResearch(researchData);
        setExperience(experienceData);
        setProjects(projectsData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching portfolio data:", error);
        // If the request failed, increment retry count and try again after delay
        setRetryCount((prev) => prev + 1);
      }
    };

    fetchData();

    // Set up retry mechanism
    const retryTimer = setTimeout(() => {
      if (loading && retryCount < 5) {
        fetchData();
      } else if (retryCount >= 5) {
        setLoading(false); // Stop loading after 5 retries
      }
    }, 5000); // Retry every 5 seconds

    return () => clearTimeout(retryTimer);
  }, [loading, retryCount]);

  /**
   * Checks if all the data for the resume section is loaded and populated.
   *
   * @returns {boolean} True if all data is loaded and populated, false otherwise.
   */
  const isDataLoaded = () => {
    return (
      !loading &&
      education.length > 0 &&
      research.length > 0 &&
      experience.length > 0 &&
      projects.length > 0
    );
  };

  if (!isDataLoaded()) {
    return <LoadingState />;
  }

  return (
    <>
      <Helmet>
        <title>Portfolio — Kaustubh Harapanahalli</title>
        <meta
          name="description"
          content="Education, research, work experience, and projects of Kaustubh Harapanahalli."
        />
      </Helmet>

      <section className="portfolio-section section-band-1">
        <Container>
          <Row>
            <Col md={1}></Col>
            <Col md={10}>
              <span className="section-label">Overview</span>
              <h2 className="font-weight-bold">Research interests.</h2>
              {researchInterests.length > 0 && (
                <div
                  className="research-pillars"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(researchInterests[0].description),
                  }}
                />
              )}
            </Col>
            <Col md={1}></Col>
          </Row>
        </Container>
      </section>

      {researchPillars.length > 0 && (
        <section className="portfolio-section section-band-2">
          <Container>
            <Row>
              <Col md={1}></Col>
              <Col md={10}>
                <span className="section-label">Focus Areas</span>
                <h2 className="font-weight-bold">Research pillars.</h2>
                <div className="research-pillars-grid">
                  {researchPillars.map((pillar) => (
                    <div key={pillar.id} className="research-pillar">
                      <h3 className="research-pillar-title">{pillar.title}</h3>
                      <div
                        className="research-pillar-description"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHTML(pillar.description),
                        }}
                      />
                    </div>
                  ))}
                </div>
              </Col>
              <Col md={1}></Col>
            </Row>
          </Container>
        </section>
      )}

      <section className="portfolio-section section-band-1">
        <Container>
          <Row>
            <Col md={1}></Col>
            <Col md={10}>
              <span className="section-label">Background</span>
              <h2 className="font-weight-bold">Education.</h2>
              <div className="education-list">
                {education.map((edu, index) => (
                  <EducationItem key={index} edu={edu} />
                ))}
              </div>
            </Col>
            <Col md={1}></Col>
          </Row>
        </Container>
      </section>

      <section className="portfolio-section section-band-2">
        <Container>
          <Row>
            <Col md={1}></Col>
            <Col md={10}>
              <span className="section-label">Publications</span>
              <h2 className="font-weight-bold">Research.</h2>
              <div className="timeline-container">
                {research.map((item, index) => (
                  <ResearchItem key={index} item={item} />
                ))}
              </div>
            </Col>
            <Col md={1}></Col>
          </Row>
        </Container>
      </section>

      <section className="portfolio-section section-band-1">
        <Container>
          <Row>
            <Col md={1}></Col>
            <Col md={10}>
              <span className="section-label">Industry</span>
              <h2 className="font-weight-bold">Work experience.</h2>
              <div className="timeline-container exp-timeline">
                {experience.map((exp, index) => (
                  <ExperienceItem key={index} exp={exp} />
                ))}
              </div>
            </Col>
            <Col md={1}></Col>
          </Row>
        </Container>
      </section>

      <section className="portfolio-section section-band-2">
        <Container>
          <Row>
            <Col md={1}></Col>
            <Col md={10}>
              <span className="section-label">Work</span>
              <h2 className="font-weight-bold">Projects.</h2>
              <div className="projects-grid">
                {projects.map((project, index) => (
                  <ProjectItem key={index} project={project} />
                ))}
              </div>
            </Col>
            <Col md={1}></Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Resume;
